import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle, Home, MessageCircle } from "lucide-react";
import { getPaymentStatus, getOrder, rupee } from "../lib/api";
import { useCart } from "../context/CartContext";
import { BRAND } from "../lib/brand";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let polls = 0;
    let timer;

    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          try { setOrder(await getOrder(sessionId)); } catch { /* ignore */ }
          return;
        }
        if (res.status === "failed" || res.status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-5 py-20">
      <div className="max-w-lg w-full text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-12 h-12 text-orange animate-spin mx-auto" />
            <p className="font-heading uppercase text-3xl mt-6">Confirming your payment…</p>
            <p className="text-cream/60 mt-2">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} data-testid="payment-confirmed">
            <img src={BRAND.mascot} alt={BRAND.name} className="h-16 w-16 mx-auto object-contain rounded-xl mb-6" />
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
              className="w-20 h-20 rounded-full bg-orange flex items-center justify-center mx-auto shadow-hard-cream"
            >
              <Check className="w-10 h-10 text-primary-foreground" strokeWidth={3} />
            </motion.div>
            <p className="font-accent text-chick text-3xl mt-6">Order confirmed!</p>
            <h1 className="font-heading uppercase text-4xl sm:text-5xl tracking-tighter mt-1">Your kukkad is on it</h1>
            <p className="text-cream/70 mt-3">We’ve received your payment and the kitchen is firing up. You’ll get a call shortly on your delivery.</p>

            {order && (
              <div className="mt-8 text-left bg-card border border-border rounded-lg p-6" data-testid="order-summary">
                <div className="flex justify-between items-center pb-3 border-b border-border">
                  <span className="text-cream/60 text-sm uppercase tracking-widest">Order</span>
                  <span className="font-mono text-sm text-orange">#{order.order_id?.slice(0, 8)}</span>
                </div>
                <div className="space-y-2 mt-3">
                  {order.lines?.map((l, i) => (
                    <div key={i} className="flex justify-between text-sm text-cream/80">
                      <span>{l.quantity} × {l.name}</span>
                      <span>{rupee(l.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-heading text-2xl uppercase mt-4 pt-3 border-t border-border">
                  <span>Paid</span><span className="text-orange">{rupee(order.amount)}</span>
                </div>
                <p className="text-cream/50 text-sm mt-3">Delivering to {order.address?.name}, {order.address?.city} - {order.address?.pincode}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <Link to="/" data-testid="success-home" className="flex items-center gap-2 bg-orange text-primary-foreground font-bold uppercase px-6 py-3.5 rounded-md hover:scale-105 transition-transform">
                <Home className="w-4 h-4" /> Back Home
              </Link>
              <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-cream/40 text-cream font-bold uppercase px-6 py-3.5 rounded-md hover:bg-cream hover:text-brown transition-colors">
                <MessageCircle className="w-4 h-4" /> Track on WhatsApp
              </a>
            </div>
          </motion.div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="payment-failed">
            <XCircle className="w-16 h-16 text-destructive mx-auto" />
            <h1 className="font-heading uppercase text-4xl tracking-tighter mt-6">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-cream/70 mt-3">
              {state === "timeout"
                ? "We couldn’t confirm your payment in time. If money was deducted, it will reflect shortly — please contact us on WhatsApp."
                : "Something went wrong with your payment. No money was charged. Please try again."}
            </p>
            <div className="flex gap-3 justify-center mt-8">
              <Link to="/checkout" data-testid="failed-retry" className="bg-orange text-primary-foreground font-bold uppercase px-6 py-3.5 rounded-md hover:scale-105 transition-transform">
                Try Again
              </Link>
              <Link to="/" className="border border-cream/40 text-cream font-bold uppercase px-6 py-3.5 rounded-md hover:bg-cream hover:text-brown transition-colors">
                Home
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
